"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { motion } from "motion/react";
import { forwardRef } from "react";
import { cleanProps, cn } from "../../../utils/index.js";
import { CdnIcon } from "../../CdnIcon/CdnIcon.js";
import { useToolbarContext } from "../Toolbar.context.js";
import { itineraryButtonVariantKeys, itineraryButtonVariants } from "./ItineraryButton.variants.js";
const ItineraryButton_ItineraryButton = /*#__PURE__*/ forwardRef(function({ as = 'button', title, data1, data1Suffix, data2, icon = 'search', buttonRight, className, onClick, align, condensed, emphasis: emphasisProp, animate, titleTruncate, data1Truncate, data1SuffixTruncate, data2Truncate, ...props }, ref) {
    const { emphasis: contextEmphasis } = useToolbarContext();
    const emphasis = emphasisProp ?? contextEmphasis;
    const hasIcon = Boolean(icon);
    const hasButtonRight = Boolean(buttonRight);
    const { root, clickableSlot, contentSlot, titleSlot, titleTextSlot, metaDataSlot, data1Slot, data1SuffixSlot, data2Slot, dotSeparatorSlot, iconSlot, iconBalanceSlot, buttonRightSlot, backgroundSlot, backgroundBlurSlot } = itineraryButtonVariants({
        align,
        condensed,
        as,
        emphasis,
        animate,
        hasIcon,
        hasButtonRight,
        titleTruncate,
        data1Truncate,
        data1SuffixTruncate,
        data2Truncate
    });
    const domProps = cleanProps(props, itineraryButtonVariantKeys);
    const MotionDiv = animate ? motion.div : 'div';
    const layoutTransition = {
        duration: 0.2,
        ease: 'easeOut'
    };
    const showIcon = hasIcon && 'div' !== as;
    const showDotSeparator = data2 && (data1 || data1Suffix);
    const ContentWrapper = animate ? motion.div : 'div';
    const TitleWrapper = animate ? motion.div : 'div';
    const MetaWrapper = animate ? motion.div : 'div';
    const contentMotionProps = animate ? {
        layout: true,
        transition: layoutTransition
    } : {};
    const innerMotionProps = animate ? {
        layout: 'position',
        transition: layoutTransition
    } : {};
    const coreContent = /*#__PURE__*/ jsxs(Fragment, {
        children: [
            showIcon && icon && /*#__PURE__*/ jsx(CdnIcon, {
                className: iconSlot(),
                iconName: icon,
                size: "24"
            }),
            /*#__PURE__*/ jsxs(ContentWrapper, {
                className: contentSlot(),
                ...contentMotionProps,
                children: [
                    title && /*#__PURE__*/ jsx(TitleWrapper, {
                        className: titleSlot(),
                        ...innerMotionProps,
                        children: /*#__PURE__*/ jsx("p", {
                            className: titleTextSlot(),
                            children: title
                        })
                    }),
                    (data1 || data2) && /*#__PURE__*/ jsxs(MetaWrapper, {
                        className: metaDataSlot(),
                        ...innerMotionProps,
                        children: [
                            data1 && /*#__PURE__*/ jsx("p", {
                                className: data1Slot(),
                                children: data1
                            }),
                            data1Suffix && /*#__PURE__*/ jsx("p", {
                                className: data1SuffixSlot(),
                                children: data1Suffix
                            }),
                            showDotSeparator && /*#__PURE__*/ jsx("p", {
                                className: dotSeparatorSlot(),
                                children: "•"
                            }),
                            data2 && /*#__PURE__*/ jsx("p", {
                                className: data2Slot(),
                                children: data2
                            })
                        ]
                    })
                ]
            }),
            showIcon && 'center' === align && !hasButtonRight && /*#__PURE__*/ jsx("div", {
                className: iconBalanceSlot(),
                "aria-hidden": "true"
            })
        ]
    });
    const motionProps = animate ? {
        layout: true,
        transition: layoutTransition
    } : {};
    return /*#__PURE__*/ jsxs(MotionDiv, {
        ref: ref,
        className: cn(root(), className),
        ...motionProps,
        ...domProps,
        children: [
            'button' === as ? /*#__PURE__*/ jsx("button", {
                type: "button",
                className: clickableSlot(),
                onClick: onClick,
                children: coreContent
            }) : coreContent,
            hasButtonRight && /*#__PURE__*/ jsx("div", {
                className: buttonRightSlot(),
                children: buttonRight
            }),
            /*#__PURE__*/ jsx("div", {
                className: backgroundSlot(),
                children: /*#__PURE__*/ jsx("div", {
                    className: backgroundBlurSlot()
                })
            })
        ]
    });
});
export { ItineraryButton_ItineraryButton as ItineraryButton };
